import type { Elf } from "./elf.ts";
import { LineTable, type LineSections } from "./line.ts";
import { Reader, stringAt } from "./reader.ts";

const DW_TAG_class_type = 0x02;
const DW_TAG_lexical_block = 0x0b;
const DW_TAG_compile_unit = 0x11;
const DW_TAG_structure_type = 0x13;
const DW_TAG_union_type = 0x17;
const DW_TAG_inlined_subroutine = 0x1d;
const DW_TAG_subprogram = 0x2e;
const DW_TAG_namespace = 0x39;
const DW_TAG_partial_unit = 0x3c;
const DW_TAG_call_site = 0x48;
const DW_TAG_GNU_call_site = 0x4109;

const DW_AT_name = 0x03;
const DW_AT_stmt_list = 0x10;
const DW_AT_low_pc = 0x11;
const DW_AT_high_pc = 0x12;
const DW_AT_comp_dir = 0x1b;
const DW_AT_abstract_origin = 0x31;
const DW_AT_specification = 0x47;
const DW_AT_ranges = 0x55;
const DW_AT_call_file = 0x58;
const DW_AT_call_line = 0x59;
const DW_AT_str_offsets_base = 0x72;
const DW_AT_addr_base = 0x73;
const DW_AT_rnglists_base = 0x74;
const DW_AT_call_return_pc = 0x7d;
const DW_AT_call_origin = 0x7f;
const DW_AT_call_pc = 0x81;
const DW_AT_call_tail_call = 0x82;
const DW_AT_GNU_tail_call = 0x2115;

const DW_UT_compile = 1;
const DW_UT_partial = 3;

const FORM_addr = 0x01;
const FORM_ref_addr = 0x10;
const FORM_sec_offset = 0x17;
const FORM_implicit_const = 0x21;
const FORM_rnglistx = 0x23;

/** A form whose value is a CU-relative reference to another entry. */
const LOCAL_REFS = new Set([0x11, 0x12, 0x13, 0x14, 0x15]);
/** Forms whose value indexes .debug_str_offsets or .debug_addr. */
const STRX = new Set([0x1a, 0x25, 0x26, 0x27, 0x28]);
const ADDRX = new Set([0x1b, 0x29, 0x2a, 0x2b, 0x2c]);
/** Forms of the constant class, which make DW_AT_high_pc a length. */
const CONSTANTS = new Set([0x05, 0x06, 0x07, 0x0b, 0x0d, 0x0f, FORM_implicit_const]);

interface Attribute {
    form: number;
    value: number | string;
}

interface Abbrev {
    tag: number;
    children: boolean;
    specs: { name: number; form: number; implicit: number }[];
}

interface Die {
    offset: number;
    tag: number;
    attrs: Map<number, Attribute>;
    parent: Die | undefined;
    children: Die[];
    hasChildren: boolean;
}

/** One source location of an address; several when functions were inlined. */
export interface Location {
    func: string;
    file: string | undefined;
    line: number | undefined;
}

/** A call a function makes, for reconstructing frames that tail calls removed. */
export interface CallSite {
    /** The address after the call, which is what a return address holds. */
    returnPc: number | undefined;
    /** The address of the call instruction, recorded for tail calls in DWARF 5. */
    callPc: number | undefined;
    origin: string | undefined;
    tail: boolean;
}

interface Sections extends LineSections {
    info: Uint8Array;
    abbrev: Uint8Array;
    strOffsets?: Uint8Array;
    addr?: Uint8Array;
    ranges?: Uint8Array;
    rnglists?: Uint8Array;
}

/**
 * A compilation unit of .debug_info. Only its first entry is read up front;
 * the entry tree and the line table are read the first time an address in
 * the unit is looked up.
 */
class Unit {
    readonly offset: number;
    readonly end: number;
    readonly version: number;
    readonly addressSize: number;
    readonly #dwarf: Dwarf;
    readonly #abbrevs: Map<number, Abbrev>;
    readonly #firstDie: number;
    readonly root: Die;
    readonly ranges: [number, number][];
    #dies: Map<number, Die> | undefined;
    #lines: LineTable | null | undefined;

    constructor(dwarf: Dwarf, offset: number) {
        this.#dwarf = dwarf;
        this.offset = offset;
        const r = new Reader(dwarf.sections.info, offset);
        const length = r.u32();
        if (length === 0xffffffff) throw new Error("64-bit DWARF is not supported");
        this.end = r.offset + length;
        this.version = r.u16();
        let unitType = DW_UT_compile;
        let abbrevOffset: number;
        if (this.version >= 5) {
            unitType = r.u8();
            this.addressSize = r.u8();
            abbrevOffset = r.u32();
            if (unitType !== DW_UT_compile && unitType !== DW_UT_partial) r.skip(8);
        } else {
            abbrevOffset = r.u32();
            this.addressSize = r.u8();
        }
        this.#firstDie = r.offset;
        this.#abbrevs = dwarf.abbrevs(abbrevOffset);
        this.root = this.#readDie(r, undefined) ?? {
            offset: this.#firstDie,
            tag: 0,
            attrs: new Map(),
            parent: undefined,
            children: [],
            hasChildren: false,
        };
        const isCode = this.root.tag === DW_TAG_compile_unit || this.root.tag === DW_TAG_partial_unit;
        this.ranges = isCode ? this.rangesOf(this.root) : [];
    }

    /** The entry at a .debug_info offset inside this unit. */
    die(offset: number): Die | undefined {
        return this.#tree().get(offset);
    }

    tree(): Die {
        this.#tree();
        return this.root;
    }

    lines(): LineTable | undefined {
        if (this.#lines === undefined) {
            const stmtList = this.#number(this.root, DW_AT_stmt_list);
            const compDir = this.string(this.root, DW_AT_comp_dir) ?? "";
            this.#lines = stmtList === undefined ? null : new LineTable(this.#dwarf.sections, stmtList, compDir);
        }
        return this.#lines ?? undefined;
    }

    string(die: Die, name: number): string | undefined {
        const attr = die.attrs.get(name);
        if (attr === undefined) return undefined;
        if (typeof attr.value === "string") return attr.value;
        const sections = this.#dwarf.sections;
        if (STRX.has(attr.form)) {
            const base = this.#number(this.root, DW_AT_str_offsets_base) ?? 8;
            if (sections.strOffsets === undefined || sections.str === undefined) return undefined;
            const at = new Reader(sections.strOffsets, base + attr.value * 4).u32();
            return stringAt(sections.str, at);
        }
        if (attr.form === 0x0e && sections.str !== undefined) return stringAt(sections.str, attr.value);
        if (attr.form === 0x1f && sections.lineStr !== undefined) {
            return stringAt(sections.lineStr, attr.value);
        }
        return undefined;
    }

    /** The .debug_info offset an attribute of reference class points at. */
    reference(die: Die, name: number): number | undefined {
        const attr = die.attrs.get(name);
        if (attr === undefined || typeof attr.value !== "number") return undefined;
        if (LOCAL_REFS.has(attr.form)) return this.offset + attr.value;
        if (attr.form === FORM_ref_addr) return attr.value;
        return undefined;
    }

    number(die: Die, name: number): number | undefined {
        return this.#number(die, name);
    }

    /** The address ranges an entry covers, from low/high pc or DW_AT_ranges. */
    rangesOf(die: Die): [number, number][] {
        const low = this.address(die, DW_AT_low_pc);
        const high = die.attrs.get(DW_AT_high_pc);
        if (low !== undefined && high !== undefined && typeof high.value === "number") {
            const end = CONSTANTS.has(high.form) ? low + high.value : this.#resolveAddress(high);
            return end === undefined ? [] : [[low, end]];
        }
        const ranges = die.attrs.get(DW_AT_ranges);
        if (ranges === undefined || typeof ranges.value !== "number") return [];
        const base = this.address(this.root, DW_AT_low_pc) ?? 0;
        if (this.version >= 5) return this.#rnglist(ranges, base);
        return this.#debugRanges(ranges.value, base);
    }

    #tree(): Map<number, Die> {
        if (this.#dies !== undefined) return this.#dies;
        const dies = new Map<number, Die>([[this.root.offset, this.root]]);
        const r = new Reader(this.#dwarf.sections.info, this.#firstDie);
        this.#readDie(r, undefined);
        const stack: Die[] = [];
        let parent = this.root.hasChildren ? this.root : undefined;
        while (parent !== undefined && r.offset < this.end) {
            const die = this.#readDie(r, parent);
            if (die === undefined) {
                parent = stack.pop();
                continue;
            }
            dies.set(die.offset, die);
            parent.children.push(die);
            if (die.hasChildren) {
                stack.push(parent);
                parent = die;
            }
        }
        return (this.#dies = dies);
    }

    #readDie(r: Reader, parent: Die | undefined): Die | undefined {
        const offset = r.offset;
        const code = r.uleb();
        if (code === 0) return undefined;
        const abbrev = this.#abbrevs.get(code);
        if (abbrev === undefined) throw new Error(`no abbreviation ${code} at 0x${offset.toString(16)}`);
        const attrs = new Map<number, Attribute>();
        for (const spec of abbrev.specs) {
            const value = this.#readForm(r, spec.form, spec.implicit);
            if (value !== undefined) attrs.set(spec.name, value);
        }
        return { offset, tag: abbrev.tag, attrs, parent, children: [], hasChildren: abbrev.children };
    }

    #readForm(r: Reader, form: number, implicit: number): Attribute | undefined {
        const value = (v: number | string): Attribute => ({ form, value: v });
        switch (form) {
            case FORM_addr:
                return value(r.sized(this.addressSize));
            case 0x03: // block2
                r.skip(r.u16());
                return undefined;
            case 0x04: // block4
                r.skip(r.u32());
                return undefined;
            case 0x09: // block
            case 0x18: // exprloc
                r.skip(r.uleb());
                return undefined;
            case 0x0a: // block1
                r.skip(r.u8());
                return undefined;
            case 0x05: // data2
            case 0x12: // ref2
            case 0x26: // strx2
            case 0x2a: // addrx2
                return value(r.u16());
            case 0x06: // data4
            case 0x13: // ref4
            case 0x0e: // strp
            case 0x1f: // line_strp
            case FORM_sec_offset:
            case 0x1c: // ref_sup4
            case 0x1d: // strp_sup
            case 0x28: // strx4
            case 0x2c: // addrx4
                return value(r.u32());
            case 0x07: // data8
            case 0x14: // ref8
            case 0x20: // ref_sig8
            case 0x24: // ref_sup8
                return value(r.u64());
            case 0x1e: // data16
                r.skip(16);
                return undefined;
            case 0x08: // string
                return value(r.cstr());
            case 0x0b: // data1
            case 0x0c: // flag
            case 0x11: // ref1
            case 0x25: // strx1
            case 0x29: // addrx1
                return value(r.u8());
            case 0x19: // flag_present
                return value(1);
            case 0x0d: // sdata
                return value(r.sleb());
            case 0x0f: // udata
            case 0x15: // ref_udata
            case 0x1a: // strx
            case 0x1b: // addrx
            case 0x22: // loclistx
            case FORM_rnglistx:
                return value(r.uleb());
            case FORM_ref_addr:
                return value(this.version <= 2 ? r.sized(this.addressSize) : r.u32());
            case 0x16: // indirect
                return this.#readForm(r, r.uleb(), implicit);
            case FORM_implicit_const:
                return value(implicit);
            case 0x27: // strx3
            case 0x2b: // addrx3
                return value(r.u24());
        }
        throw new Error(`unsupported form 0x${form.toString(16)}`);
    }

    #number(die: Die, name: number): number | undefined {
        const value = die.attrs.get(name)?.value;
        return typeof value === "number" ? value : undefined;
    }

    address(die: Die, name: number): number | undefined {
        const attr = die.attrs.get(name);
        return attr === undefined ? undefined : this.#resolveAddress(attr);
    }

    #resolveAddress(attr: Attribute): number | undefined {
        if (typeof attr.value !== "number") return undefined;
        if (ADDRX.has(attr.form)) return this.#indexedAddress(attr.value);
        return attr.value;
    }

    #indexedAddress(index: number): number | undefined {
        const addr = this.#dwarf.sections.addr;
        const base = this.#number(this.root, DW_AT_addr_base) ?? 8;
        if (addr === undefined) return undefined;
        return new Reader(addr, base + index * this.addressSize).sized(this.addressSize);
    }

    #debugRanges(offset: number, cuBase: number): [number, number][] {
        const section = this.#dwarf.sections.ranges;
        if (section === undefined) return [];
        const r = new Reader(section, offset);
        const all = this.addressSize === 4 ? 0xffffffff : Number.MAX_SAFE_INTEGER;
        const out: [number, number][] = [];
        let base = cuBase;
        while (!r.done) {
            const start = r.sized(this.addressSize);
            const end = r.sized(this.addressSize);
            if (start === 0 && end === 0) break;
            if (start === all) base = end;
            else if (start !== end) out.push([base + start, base + end]);
        }
        return out;
    }

    #rnglist(attr: Attribute, cuBase: number): [number, number][] {
        const section = this.#dwarf.sections.rnglists;
        if (section === undefined || typeof attr.value !== "number") return [];
        let offset = attr.value;
        if (attr.form === FORM_rnglistx) {
            const base = this.#number(this.root, DW_AT_rnglists_base) ?? 12;
            offset = base + new Reader(section, base + attr.value * 4).u32();
        }
        const r = new Reader(section, offset);
        const out: [number, number][] = [];
        const push = (start: number | undefined, end: number | undefined) => {
            if (start !== undefined && end !== undefined && start !== end) out.push([start, end]);
        };
        let base: number | undefined = cuBase;
        while (!r.done) {
            const kind = r.u8();
            if (kind === 0) break; // DW_RLE_end_of_list
            if (kind === 1) {
                base = this.#indexedAddress(r.uleb());
            } else if (kind === 2) {
                push(this.#indexedAddress(r.uleb()), this.#indexedAddress(r.uleb()));
            } else if (kind === 3) {
                const start = this.#indexedAddress(r.uleb());
                const length = r.uleb();
                push(start, start === undefined ? undefined : start + length);
            } else if (kind === 4) {
                const start = r.uleb();
                const end = r.uleb();
                if (base !== undefined) push(base + start, base + end);
            } else if (kind === 5) {
                base = r.sized(this.addressSize);
            } else if (kind === 6) {
                push(r.sized(this.addressSize), r.sized(this.addressSize));
            } else if (kind === 7) {
                const start = r.sized(this.addressSize);
                push(start, start + r.uleb());
            } else {
                break;
            }
        }
        return out;
    }
}

/**
 * The debug info of an ELF file, answering what `addr2line -fi` answers: the
 * function and source line of an address, and the functions it was inlined
 * into.
 */
export class Dwarf {
    readonly sections: Sections;
    readonly #units: Unit[] = [];
    readonly #abbrevCache = new Map<number, Map<number, Abbrev>>();

    constructor(elf: Elf) {
        const info = elf.section(".debug_info");
        const abbrev = elf.section(".debug_abbrev");
        const line = elf.section(".debug_line");
        if (info === undefined || abbrev === undefined || line === undefined) {
            throw new Error("the ELF file has no debug info");
        }
        this.sections = {
            info,
            abbrev,
            line,
            str: elf.section(".debug_str"),
            lineStr: elf.section(".debug_line_str"),
            strOffsets: elf.section(".debug_str_offsets"),
            addr: elf.section(".debug_addr"),
            ranges: elf.section(".debug_ranges"),
            rnglists: elf.section(".debug_rnglists"),
        };
        for (let offset = 0; offset < info.length;) {
            const unit = new Unit(this, offset);
            this.#units.push(unit);
            offset = unit.end;
        }
    }

    abbrevs(offset: number): Map<number, Abbrev> {
        const cached = this.#abbrevCache.get(offset);
        if (cached !== undefined) return cached;
        const table = new Map<number, Abbrev>();
        const r = new Reader(this.sections.abbrev, offset);
        for (let code = r.uleb(); code !== 0; code = r.uleb()) {
            const tag = r.uleb();
            const children = r.u8() !== 0;
            const specs: Abbrev["specs"] = [];
            for (;;) {
                const name = r.uleb();
                const form = r.uleb();
                if (name === 0 && form === 0) break;
                specs.push({ name, form, implicit: form === FORM_implicit_const ? r.sleb() : 0 });
            }
            table.set(code, { tag, children, specs });
        }
        this.#abbrevCache.set(offset, table);
        return table;
    }

    /** Innermost location first; empty if no unit covers the address. */
    locate(address: number): Location[] {
        const found = this.#chain(address);
        if (found === undefined) return [];
        const { unit, chain } = found;
        const lines = unit.lines();
        const here = lines?.find(address);
        if (chain.length === 0) {
            return here === undefined ? [] : [{ func: "??", file: here.file, line: here.line }];
        }
        const out: Location[] = [];
        let file = here?.file;
        let line = here?.line;
        for (let i = chain.length - 1; i >= 0; i--) {
            const die = chain[i]!;
            out.push({ func: this.#name(unit, die) ?? "??", file, line });
            const callFile = unit.number(die, DW_AT_call_file);
            file = callFile === undefined ? undefined : lines?.file(callFile);
            line = unit.number(die, DW_AT_call_line);
        }
        return out;
    }

    /** The name of the function whose code the address is in, inlining aside. */
    functionAt(address: number): string | undefined {
        const found = this.#chain(address);
        const die = found?.chain[0];
        return die === undefined ? undefined : this.#name(found!.unit, die);
    }

    /** The calls the function containing the address makes, as DW_TAG_call_site records them. */
    callSites(address: number): CallSite[] {
        const found = this.#chain(address);
        const die = found?.chain[0];
        if (die === undefined) return [];
        const sites: CallSite[] = [];
        const visit = (parent: Die) => {
            for (const child of parent.children) {
                if (child.tag === DW_TAG_call_site || child.tag === DW_TAG_GNU_call_site) {
                    sites.push(this.#callSite(found!.unit, child));
                } else if (child.children.length > 0) {
                    visit(child);
                }
            }
        };
        visit(die);
        return sites;
    }

    #callSite(unit: Unit, die: Die): CallSite {
        const gnu = die.tag === DW_TAG_GNU_call_site;
        const origin = unit.reference(die, gnu ? DW_AT_abstract_origin : DW_AT_call_origin);
        const target = origin === undefined ? undefined : this.#dieAt(origin);
        return {
            returnPc: unit.address(die, gnu ? DW_AT_low_pc : DW_AT_call_return_pc),
            callPc: gnu ? undefined : unit.address(die, DW_AT_call_pc),
            origin: target === undefined ? undefined : this.#name(target.unit, target.die),
            tail: die.attrs.has(gnu ? DW_AT_GNU_tail_call : DW_AT_call_tail_call),
        };
    }

    #chain(address: number): { unit: Unit; chain: Die[] } | undefined {
        const unit = this.#units.find((u) => u.ranges.some(([s, e]) => address >= s && address < e));
        if (unit === undefined) return undefined;
        const chain: Die[] = [];
        this.#descend(unit, unit.tree(), address, chain);
        return { unit, chain };
    }

    /** Collects the subprogram and the inlined subroutines that cover the address. */
    #descend(unit: Unit, die: Die, address: number, chain: Die[]): void {
        const depth = chain.length;
        for (const child of die.children) {
            const tag = child.tag;
            if (tag === DW_TAG_subprogram || tag === DW_TAG_inlined_subroutine) {
                if (!this.#covers(unit, child, address)) continue;
                chain.push(child);
                this.#descend(unit, child, address, chain);
                return;
            }
            if (tag === DW_TAG_lexical_block) {
                const ranges = unit.rangesOf(child);
                if (ranges.length > 0 && !this.#covers(unit, child, address)) continue;
                this.#descend(unit, child, address, chain);
            } else if (isScope(tag)) {
                this.#descend(unit, child, address, chain);
            }
            if (chain.length > depth) return;
        }
    }

    #covers(unit: Unit, die: Die, address: number): boolean {
        return unit.rangesOf(die).some(([start, end]) => address >= start && address < end);
    }

    /** A function's name, qualified by the namespaces and classes around its declaration. */
    #name(unit: Unit, die: Die): string | undefined {
        let current: { unit: Unit; die: Die } = { unit, die };
        for (let hops = 0; hops < 8; hops++) {
            const name = current.unit.string(current.die, DW_AT_name);
            if (name !== undefined) return this.#qualify(current.unit, current.die, name);
            const next =
                current.unit.reference(current.die, DW_AT_abstract_origin) ??
                current.unit.reference(current.die, DW_AT_specification);
            if (next === undefined) return undefined;
            const found = this.#dieAt(next);
            if (found === undefined) return undefined;
            current = found;
        }
        return undefined;
    }

    #qualify(unit: Unit, die: Die, name: string): string {
        const parts = [name];
        for (let scope = die.parent; scope !== undefined; scope = scope.parent) {
            if (!isScope(scope.tag)) continue;
            parts.unshift(
                unit.string(scope, DW_AT_name) ??
                    (scope.tag === DW_TAG_namespace ? "(anonymous namespace)" : "(anonymous)"),
            );
        }
        return parts.join("::");
    }

    #dieAt(offset: number): { unit: Unit; die: Die } | undefined {
        const unit = this.#units.find((u) => offset >= u.offset && offset < u.end);
        const die = unit?.die(offset);
        return unit === undefined || die === undefined ? undefined : { unit, die };
    }
}

function isScope(tag: number): boolean {
    return (
        tag === DW_TAG_namespace ||
        tag === DW_TAG_class_type ||
        tag === DW_TAG_structure_type ||
        tag === DW_TAG_union_type
    );
}
